import * as React from 'react';
import constants from 'assets/constants';
import { useFavorites } from './favorites';
import i18n from 'i18n';
import { apiFetch, apiUrl, currentLocale, useLocale } from './api';

export { apiFetch, apiUrl, currentLocale, useLocale };

const RANK_MALE = [100, 200, 300, 400, 500, 700, 1000];
const RANK_FEMALE = [50, 100, 200, 300, 400, 500, 700];
export function next_rank(rank, is_female) {
    const ar = is_female ? RANK_FEMALE : RANK_MALE;

    for (var i = 0; i < ar.length - 1; i++) {
        if (rank == ar[i]) {
            return ar[i+1];
        }
    }

    return ar[ar.length - 1];
}

export function getMaleRanks() {
    return RANK_MALE;
}

export function getFemaleRanks() {
    return RANK_FEMALE;
}

export function prev_rank(rank, is_female) {
    const ar = is_female ? RANK_FEMALE : RANK_MALE;

    for (var i = 1; i < ar.length; i++) {
        if (rank == ar[i]) {
            return ar[i-1];
        }
    }
    return ar[0];
}

export function useFirstRender() {
    const ref = React.useRef(true);
    const firstRender = ref.current;
    ref.current = false;
    return firstRender;
}

export function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
            if (window.pageYOffset.toFixed() === fixedOffset) {
                window.removeEventListener('scroll', onScroll)
                callback()
            }
        }

    window.addEventListener('scroll', onScroll)
    onScroll()
    window.scrollTo({
        top: offset,
        behavior: 'smooth'
    })
}

// Keep existing page data on access failures. The shared client displays the
// recoverable error; completion callbacks release page loading flags.
export async function refreshData(refresh_url, callback, onFail) {
    try {
        const response = await apiFetch(apiUrl(refresh_url));
        callback(await response.json());
    } catch (error) {
        console.error("Failed to refresh report:", error);
        onFail?.();
    }
}

export async function fetchData(playerId, callback, season, onFail) {
    try {
        const response = await apiFetch(apiUrl("/get_player_report/" + playerId + (season ? "?s=" + season : "")));
        callback(await response.json());
    } catch (error) {
        console.error("Failed to fetch report:", error);
        onFail?.();
    }
}

export async function fixData(playerId, callback) {
    try {
        const response = await apiFetch(apiUrl("/fixme/" + playerId));
        callback(await response.json());
        return true;
    } catch (error) {
        console.error("Failed to fix report:", error);
        return false;
    }
}

const chartCache = new Map(); // locale -> {promise, data}
export function useChartData() {
    const locale = useLocale();

    const [chartData, setChartData] = React.useState(() => chartCache.get(locale)?.data ?? {});

    React.useEffect(() => {
        let cancelled = false;

        let entry = chartCache.get(locale);
        if (!entry) {
            const promise = apiFetch(apiUrl("/get_charts")).then(response => response.json());
            entry = {promise, data: null};
            chartCache.set(locale, entry);
            promise.then(data => {entry.data = data;}).catch(() => chartCache.delete(locale));
        }

        entry.promise.then(data => {
            if (!cancelled) setChartData(data);
        }).catch(err => console.error("Failed to fetch chart data:", err));

        return () => { cancelled = true; };
    }, [locale]);

    return chartData;
}

export function tokenSearch(list, s, name_field = "name", club_field = "name_club") {
    const tokens = s.split(" ");

    if (list === undefined || list == null) {
        return []
    }

    return list.filter(player => {
        const n = stripAccents(player[name_field]);
        const c = stripAccents(player[club_field].replace(".", ""));

        for (var i = 0; i < tokens.length; ++i) {
            const token = tokens[i];
            
            if (!n.includes(stripAccents(token)) && !c.includes(stripAccents(token))) {
                return false;
            }
        }

        return true;
    })
}

const simulationCache = new Map();
/*
shape:
url -> {
  promise: Promise|null,
  data: any[]|null
}
*/

export function useSimulationData(playerId, search) {
  const locale = useLocale();
  const favorites = useFavorites();
  const allOpponents = favorites.map((f) => f.playerId).filter((id) => id !== playerId);

  const normalizedSearch = React.useMemo(() => {
    return (search ?? "").trim();
  }, [search]);

  const searchKey = React.useMemo(() => {
    return normalizedSearch.toLowerCase().slice(0, 5);
  }, [normalizedSearch]);

  const useBackendSearch = searchKey.length >= 5;

  const name_field = "o_name";
  const club_field = "o_name_club";

  const opponentsKey = React.useMemo(() => {
    return allOpponents.join(",");
  }, [allOpponents]);

  const url = React.useMemo(() => {
    return apiUrl(
      "/simulate/" +
      playerId +
      (useBackendSearch
        ? `?s=${encodeURIComponent(searchKey)}`
        : `?opponents=${opponentsKey}`)
    );
  }, [playerId, useBackendSearch, searchKey, opponentsKey, locale]);

  const [simulationDataState, setSimulationDataState] = React.useState(() => {
    const cached = simulationCache.get(url);
    if (cached?.data != null) {
      return tokenSearch(cached.data, normalizedSearch, name_field, club_field);
    }
    return [];
  });

  const [fetching, setFetching] = React.useState(() => {
    const cached = simulationCache.get(url);
    return !!cached?.promise && cached?.data == null;
  });

  React.useEffect(() => {
    let cancelled = false;

    const cached = simulationCache.get(url);

    if (cached?.data != null) {
      setFetching(false);
      setSimulationDataState(
        tokenSearch(cached.data, normalizedSearch, name_field, club_field)
      );
      return;
    }

    if (!cached) {
      const promise = apiFetch(url)
        .then((response) => response.json())
        .then((data) => {
          const current = simulationCache.get(url);
          simulationCache.set(url, {
            promise: null,
            data,
          });
          return data;
        })
        .catch((err) => {
          simulationCache.delete(url);
          throw err;
        });

      simulationCache.set(url, {
        promise,
        data: null,
      });
    }

    setFetching(true);

    simulationCache.get(url).promise.then((data) => {
      if (cancelled) return;

      setFetching(false);
      setSimulationDataState(
        tokenSearch(data, normalizedSearch, name_field, club_field)
      );
    }).catch(error => {
      if (!cancelled) setFetching(false);
      console.error("Failed to fetch simulation data:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [url, normalizedSearch]);

  return [simulationDataState, fetching];
}

export function useHead2Head(playerId, opponentId, partnerId, opponentPartnerId) {
    const locale = useLocale();
    const [h2hData, setH2hData] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;
        
        if (!playerId || !opponentId) {
            setH2hData(null);
            return;
        }
        
        apiFetch(apiUrl(
            `/h2h/${playerId}?opponent=${opponentId}` + (partnerId ? '&partner=' + partnerId : '') + (opponentPartnerId ? '&opponent_partner=' + opponentPartnerId : '')
        ))
            .then((response) => response.json())
            .then((data) => {
                if (cancelled) return;
                setH2hData(data);
            })
            .catch((err) => {
                console.error("Failed to fetch head-to-head data:", err);
            });

        return () => {
            cancelled = true;
        };
    }, [playerId, opponentId, partnerId, opponentPartnerId, locale]);

    return h2hData;
}

export async function getRecentResults(players) {
    const data = await apiFetch(apiUrl("/recent_results"), {
        method: "POST", 
        body: JSON.stringify({players: players}),
        headers: {
            "Content-Type": "application/json",
        }
    }) 
    if (data.status == 200) {
        return await data.json()
    }
    return null
}

export function stripAccents(s) {
    return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
}

// first name + first initial
export function getFirstName(username) {
    if (!username) {
        return ''
    }

    const sp = username.split(' ');
    if (sp.length === 0) {
        return ''
    }
    else if (sp.length === 1) {
        return sp[0]
    } else {
        return sp[sp.length - 1] + " " + sp[0][0]
    }
}

// "Van der Stuyft Mattias" -> "M. Van der Stuyft"
export function getFirstInitialAndLastName(username) {
    if (!username) {
        return ''
    }

    const parts = username.trim().split(/\s+/)

    if (parts.length === 1) {
        return `${parts[0][0].toUpperCase()}.`
    }

    const firstName = parts.pop()
    const lastName = parts.join(' ')

    return `${firstName[0].toUpperCase()}. ${lastName}`
}

export function getInitials(username) {
    if (!username) {
        return ''
    }

    const parts = username.trim().split(/\s+/)

    if (parts.length === 1) {
        return `${parts[0][0].toUpperCase()}`
    }

    const firstName = parts.pop()
    const lastName = parts.join(' ')

    return `${lastName[0].toUpperCase()}${firstName[0].toUpperCase()}`
}

export function getLastName(username) {
    if (!username) {
        return ''
    }

    const sp = username.split(' ');
    if (sp.length === 0) {
        return ''
    }
    else if (sp.length === 1) {
        return sp[0]
    } else {
        return sp.slice(0, sp.length - 1).join(" ")
    }
}

// swap first and last name
export function swapName(username) {
    if (!username) {
        return ''
    }

    const sp = username.split(' ');
    if (sp.length === 0) {
        return ''
    }
    else if (sp.length === 1) {
        return username
    } else {
        return sp[sp.length - 1] + " " + sp.slice(0, sp.length - 1).join(" ")
    }
}

export function shortenUsername(username, maxLength = 20) {
    if (!username) {
        return ''
    }

    if (username.length <= maxLength) {
        return swapName(username)
    } else {
        return getFirstInitialAndLastName(username)
    }
}

export async function fetchUserList(s, activeOnly = false) {
        const data = await apiFetch(apiUrl("/list_players?s=" + s + "&activeOnly=" + (activeOnly ? "1" : "0")));
        const content = await data.json();
        
        return content;
}

export function useSearch(search, activeOnly = false) {
    const [results, setResults] = React.useState([]);
    const [filteredResults, setFilteredResults] = React.useState([]);

    const searchKey = React.useMemo(() => {
        return (search ?? "").trim().toLowerCase().slice(0, 5);
    }, [search]);

    React.useEffect(() => {
        let cancelled = false;

        if (!search || search.length < 5) {
            setResults([]);
            return;
        }

        fetchUserList(searchKey, activeOnly).then((data) => {
            if (cancelled) return;
            setResults(data);
        }).catch(error => console.error("Failed to search players:", error));

        return () => {
            cancelled = true;
        };
    }, [searchKey]);

    React.useEffect(() => {
        if (!search || search.length < 5) {
            setFilteredResults([]);
            return;
        }

        setFilteredResults(tokenSearch(results, search));
    }, [search, results]);

    return filteredResults;
}

export function useActivity(playerIds) {
    const locale = useLocale();
    const [activityData, setActivityData] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;

        if (!playerIds || playerIds.length === 0) {
            setActivityData(null);
            return;
        }

        apiFetch(apiUrl("/activity"), {
            method: "POST", 
            body: JSON.stringify({players: playerIds}),
            headers: {
                "Content-Type": "application/json",
            }
        }).then((response) => response.json())
            .then((data) => {
                if (cancelled) return;
                setActivityData(data);
            })
            .catch((err) => {
                console.error("Failed to fetch activity data:", err);
            });

        return () => {
            cancelled = true;
        };
    }, [playerIds, locale]);

    return activityData;
}

export function formatDateDifference(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();

  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffMs = startOfToday - startOfDate;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return i18n.t("dates.today");
  if (diffDays >= 1 && diffDays < 7) return i18n.t("dates.daysAgo", {count: diffDays});
  if (diffDays >= 7 && diffDays < 30) return i18n.t("dates.weeksAgo", {count: Math.floor(diffDays / 7)});
  if (diffDays >= 30 && diffDays < 365) return i18n.t("dates.monthsAgo", {count: Math.floor(diffDays / 30)});
  if (diffDays >= 365) return i18n.t("dates.yearsAgo", {count: Math.floor(diffDays / 365)});
  if (diffDays === -1) return i18n.t("dates.tomorrow");
  return i18n.t("dates.inDays", {count: Math.abs(diffDays)});
}